import { useMemo } from "react";
import { useAppSelector } from "../redux/hook";
import { SolicitorTypeEnum } from "../models/enum/solicitor-type.enum";
import { LoanTypeEnum } from "../models/enum/loan-type.enum";
import { R2stepperItemsStageDCurrent, R2stepperItemsStageDCurrentProposed, R2stepperItemsStageDProposed, stepperItemsStageD } from "../utils/shared-dropdown";
import { StructureStatusEnum } from "../models/enum/structure-status.enum";


function useStageDStepper(loginUserUinfin: String, application_id: string) {
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const applicationStep3FormData = useAppSelector(state => state.app.ApplicationStep3Slice.formData);
  const applicationStartFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);

  const isIncoming = applicationStep1FormData?.household?.incoming_owners?.find(e => e?.myinfo?.uinfin === loginUserUinfin);

  const isCurrentSolicitorHDB = useMemo(() => {
    return (
      applicationStep3FormData.current_solicitor_type === SolicitorTypeEnum.hdb_solicitor ||
      applicationStep3FormData.current_solicitor_type === SolicitorTypeEnum.conveyancing_hdb_solicitor
    );
  }, [applicationStep3FormData.current_solicitor_type])

  const isProposedSolicitorHDB = useMemo(() => {
    return (
      applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.hdb_solicitor ||
      applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.conveyancing_hdb_solicitor
    );
  }, [applicationStep3FormData.proposed_solicitor_type])

  const isPrivateCase = useMemo(() => {
    return (
      applicationStep2FormData.current_loan_type !== LoanTypeEnum.hdb_loan &&
      applicationStep2FormData.new_loan_type !== LoanTypeEnum.hdb_loan &&
      !isCurrentSolicitorHDB &&
      !isProposedSolicitorHDB
    );
  }, [applicationStep2FormData.current_loan_type, applicationStep2FormData.new_loan_type, isCurrentSolicitorHDB, isProposedSolicitorHDB]);

  const stepperItems = useMemo(() => {
    // Check if both the current and new loan types are NOT HDB loans
    // and if neither the current nor the proposed solicitor is HDB.
    if (isPrivateCase) {
      // If the loan is not HDB-related, return the default stepper items for Stage D
      // Add
      return stepperItemsStageD;
    } else {
      // Check if the selected HDB property has additional roles
      const hasAdditionalRoles = applicationStartFormData.selected_hdb_property?.additional_roles.length !== undefined && applicationStartFormData.selected_hdb_property?.additional_roles.length > 0;
      if (hasAdditionalRoles) {
        // If additional roles exist:
        // - Return `R2stepperItemsStageDCurrentProposed` if the logged-in user is an incoming user
        // - Otherwise, return `R2stepperItemsStageDCurrent`
        return isIncoming ? R2stepperItemsStageDCurrentProposed : R2stepperItemsStageDCurrent;
      }

      if (isIncoming) {
        // If no additional roles but the logged-in user is incoming:
        // - Return `R2stepperItemsStageDCurrentProposed` if the structure status is "retaining"
        // - Otherwise, return `R2stepperItemsStageDProposed`
        return isIncoming.structure_status === StructureStatusEnum.retaining
          ? R2stepperItemsStageDCurrentProposed
          : R2stepperItemsStageDProposed;
      }

      // Default case: If no additional roles and the logged-in user is not incoming, return `R2stepperItemsStageDCurrent`
      return R2stepperItemsStageDCurrent;
    }
  }, [applicationStartFormData, isIncoming, isPrivateCase]);

  const stageD3RedirectURL = useMemo(() => {
    // Find the step item with an id of 4 and retrieve its title
    const stageDStep3Title = stepperItems.find(item => item.id === 4)?.title;

    // Determine the redirect URL based on the title of the step
    switch (stageDStep3Title) {
      case "Financial Plan":
        return `/application-coo/stage-d/review-financial-plan?application_id=${application_id}`;
      case "Current Owners' Financial Plan":
        return `/application-coo/stage-d/review-financial-plan-current?application_id=${application_id}`;
      default:
        // Fallback URL for any other title
        return `/application-coo/stage-d/review-financial-plan-proposed?application_id=${application_id}`;
    }
  }, [stepperItems, application_id]);


  return { stepperItems, stageD3RedirectURL, isPrivateCase };
}

export default useStageDStepper